/**
 * Payment-Link Link-Tester — Fingerprint-Framework (Spec 501, Phase 2).
 *
 * Datengetriebene Diagnose (Spec §Fingerprint-Katalog, „Mistake-Fingerprinting"):
 * das Framework kennt KEINE konkrete Fehler-Variante. Es nimmt die
 * Transform-Liste der Format-Definition entgegen, rechnet jede Variante
 * clientseitig durch (~12 × WebCrypto < 10 ms) und meldet, welche die gelieferte
 * Signatur trifft. Die konkreten Varianten V1–V12 kommen als Instanzen mit der
 * Format-Definition — hier steht nur die Pipeline.
 *
 * Verallgemeinert das erprobte `computeVariantHints`-Muster (Devportal Spec 266,
 * nur Lese-Referenz) auf die Link-Dimension.
 */
import { computeHmacSha256Hex } from './hmac-engine';
import type {
  PaymentLinkFormatDefinition,
  SignatureInputs,
} from './format-definition';

/** Ein Fingerprint-Treffer: die Variante, deren Signatur zur gelieferten passt. */
export interface FingerprintResult {
  id: string;
  /** i18n-Key der Diagnose-Copy (Hilfe-Ton). */
  diagnoseKey: string;
}

/**
 * Rechnet alle Fehler-Varianten der Format-Definition durch und meldet die, deren
 * Signatur `receivedHex` entspricht.
 *
 * - `receivedHex` muss BEREITS normalisiert sein (`normalizeSignature`, lowercase,
 *   64 Hex) — das Framework urteilt nicht ueber Eingabefehler.
 * - Im Teaser (`format.available === false`) gibt es keine Diagnose → leere Liste.
 * - Passt die kanonische Signatur (gueltig) → leere Liste (kein Fingerprinting noetig).
 * - Jede Transform laeuft in ihrem eigenen try/catch: eine kaputte Variante bricht
 *   den Lauf NICHT ab (never-throw pro Variante).
 */
export async function computeFingerprints(
  format: PaymentLinkFormatDefinition,
  salt: string,
  inputs: SignatureInputs,
  receivedHex: string,
): Promise<FingerprintResult[]> {
  if (!format.available) {
    return [];
  }

  const canonicalMessage = format.buildStringToSign(inputs);
  const expected = await computeHmacSha256Hex(salt, canonicalMessage);
  if (expected === receivedHex) {
    return [];
  }

  const hits: FingerprintResult[] = [];
  for (const variant of format.transforms) {
    try {
      const alt = variant.transform(inputs, salt);
      if (!alt) {
        continue;
      }
      // `keyBytes` (roher Byte-Schlüssel) hat Vorrang vor `key` (Text) — nötig für
      // den hex-dekodierten Salt (High-Bytes). Sonst der kanonische Text-Salt.
      const key: string | Uint8Array = alt.keyBytes ?? alt.key ?? salt;
      const message = alt.stringToSign ?? canonicalMessage;
      const variantHex = await computeHmacSha256Hex(key, message);
      if (variantHex === receivedHex) {
        hits.push({ id: variant.id, diagnoseKey: variant.diagnoseKey });
      }
    } catch {
      // Never-throw pro Variante: eine fehlerhafte Transform darf die uebrigen
      // Diagnosen nicht verhindern.
      continue;
    }
  }
  return hits;
}
